import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface Message {
  _id: string;
  conversationId: string;
  senderId: string;
  recipientId: string;
  content: string;
  parentMessageId?: string | null;
  forwardedMessageId?: string | null;
  messageType?: string;
  createdAt: string;
  updatedAt?: string;
}

export type SendMessagePayload = {
  conversationId: string;
  recipientId: string;
  content: string;
  parentMessageId?: string | null;
  forwardedMessageId?: string | null;
};

export type SendMessageResponse = {
  success: boolean;
  message: string;
  data: Message;
};

const sendMessage = async (
  payload: SendMessagePayload
): Promise<SendMessageResponse> => {
  const { data } = await customAxios.post<SendMessageResponse>(
    "/chat/messages",
    payload
  );
  return data;
};

type ConversationCache =
  | {
      id?: string;
      lastMessage?: Message;
      updatedAt?: string;
    }
  | undefined;

export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation<SendMessageResponse, AxiosError, SendMessagePayload>({
    mutationFn: sendMessage,
    onSuccess: async (res, variables) => {
      showNotification({
        title: "Message sent",
        message: "",
        color: "green",
      });

      await queryClient.invalidateQueries([
        "messages",
        variables.conversationId,
        1,
        20,
      ]);

      queryClient.setQueryData<Message[] | undefined>(
        ["messages", variables.conversationId],
        (old) => (old ? [...old, res.data] : [res.data])
      );

      queryClient.setQueryData<ConversationCache>(
        ["conversation", variables.conversationId],
        (old) =>
          old
            ? {
                ...old,
                lastMessage: res.data,
                updatedAt: res.data.createdAt,
              }
            : old
      );
    },
    onError: (error) =>
      showNotification({
        title: "Error sending message",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to send message",
        color: "red",
      }),
  });
};
